import { Box, FileUpload, Grid, Link, Typography } from 'apollo-react';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import ChangedItemsModal from './ChangedItemsModal';
import IncludeExcludeUsers from './IncludeExcludeUsers';
import TransferList from './TransferList';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { EXPORT_GUIDE, FILE_UPLOAD_ALLOWED_TYPES } from '../../constants/export.constants';
import { getWorkflows } from '../../selectors/export.selectors';
import InfoBlock from '../common/InfoBlock';
import DateInput from '../controls/DateInput';

export const IMPORT_STEPS = {
  START_EXPORT: 0,
  PREVIEW: 1,
  EXPORT_PROCESSING: 2,
  EXPORT_COMPLETED: 3,
};

const StartExport = ({
  exportData,
  updateExportData,
  changedItemsList = [],
  handleAddActor,
  handleSelectActor,
  handleDeleteActor,
}) => {
  const translateString = useTranslation();

  const [importErrorMessage, setImportErrorMessage] = useState();
  const [showChangedItemsModal, setShowChangedItemsModal] = useState(false);

  const workflows = useSelector(getWorkflows);

  const handleFileUpload = ([file]) => {
    const fileType = file.name.split('.').pop();

    if (!FILE_UPLOAD_ALLOWED_TYPES.includes(fileType)) {
      setImportErrorMessage('You can upload only a Component List File (*.clf)');
      return;
    } else {
      setImportErrorMessage(null);
    }

    updateExportData('files', [file]);
  };

  const handleDeleteFile = () => {
    if (!exportData.files.length) {
      return;
    }

    updateExportData('files', []);
  };

  const handleSelectWorkflows = (workflows = []) => {
    updateExportData('selectedWorkflows', workflows);
  };

  const handleSelectDate = (date) => {
    updateExportData('selectedDate', date);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography dangerouslySetInnerHTML={{ __html: EXPORT_GUIDE.steps.a }} />
      </Grid>
      <Grid item xs={12}>
        <FileUpload
          maxItems={1}
          fullWidth
          value={exportData.files}
          onFileDelete={handleDeleteFile}
          onUpload={handleFileUpload}
          dropAreaLabels={{
            activeText: translateString('Drop here to upload!'),
            browseLinkText: translateString('browse').toLowerCase(),
            defaultText: translateString('Drag files here or '),
            hoverText: translateString('Click to browse'),
            maxText: translateString('You can import only one file'),
          }}
        />
        {importErrorMessage && <div style={{ color: 'red' }}>{importErrorMessage}</div>}
      </Grid>
      <Grid item xs={12}>
        <Typography dangerouslySetInnerHTML={{ __html: EXPORT_GUIDE.steps.b }} />
      </Grid>
      <Grid item xs={12}>
        <Box display="flex" justifyContent="flex-start">
          <TransferList
            listData={workflows}
            selectedItems={exportData.selectedWorkflows}
            leftTitle="Available Workflows"
            rightTitle="Workflows to AutoDiscover"
            handleSelectItems={handleSelectWorkflows}
          />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Typography>{EXPORT_GUIDE.steps.c}</Typography>
        <DateInput date={exportData.selectedDate} handleSelectDate={handleSelectDate} />
      </Grid>

      <Grid item xs={12}>
        <InfoBlock>
          <Typography color="textSecondary">
            {translateString('At a time, you can only select either Include User or Exclude User.')}
          </Typography>
        </InfoBlock>
      </Grid>

      <Grid item xs={8}>
        <IncludeExcludeUsers
          exportData={exportData}
          handleAddActor={handleAddActor}
          handleSelectActor={handleSelectActor}
          handleDeleteActor={handleDeleteActor}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography display="inline">
          <Link color="inherit" underline="hover" onClick={() => setShowChangedItemsModal(true)}>
            {translateString('Click Here')}
          </Link>{' '}
          <span dangerouslySetInnerHTML={{ __html: EXPORT_GUIDE.clickHereText }} />
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography
          component="span"
          display="block"
          dangerouslySetInnerHTML={{ __html: EXPORT_GUIDE.steps.d }}
        />
      </Grid>
      <Grid item xs={12}>
        <InfoBlock type="warning">
          <Typography
            color="textSecondary"
            dangerouslySetInnerHTML={{ __html: EXPORT_GUIDE.warning }}
          />
        </InfoBlock>
      </Grid>
      {showChangedItemsModal && (
        <ChangedItemsModal
          onClose={() => setShowChangedItemsModal(false)}
          changedItemsList={changedItemsList}
          selectedChangedItems={exportData.selectedChangedItems}
          updateExportData={updateExportData}
        />
      )}
    </Grid>
  );
};
export default StartExport;
